<script setup lang="ts">
import { ref } from "vue";
import { useI18n } from "vue-i18n";
import { getDisplayName } from "../../utils/GeneralUtils.ts";
import { i18n } from "../../main.ts";

const { t } = useI18n();

defineProps({
    productNo: {
        type: String,
        required: true,
    },
    profileLanguages: {
        type: Array as () => string[],
        default: () => [],
    },
    questionnaireLanguages: {
        type: Array as () => string[],
        default: () => [],
    },
    trainerLanguage: {
        type: String,
        default: "",
    },
    toggleId: {
        type: String,
        default: "",
    },
    fieldsetId: {
        type: String,
        default: "",
    },
    alwaysExpanded: {
        type: Boolean,
        default: false,
    },
});

const expanded = ref(false);

function toggle() {
    expanded.value = !expanded.value;
}

const tagPt = () => ({ root: { style: { background: "var(--p-surface-200)", color: "var(--p-surface-500)", borderRadius: "3px", fontWeight: "normal" } } });
</script>

<template>
    <!-- Always-expanded mode: fieldset with no toggle link -->
    <template v-if="alwaysExpanded">
        <Fieldset :id="fieldsetId">
            <template #legend>
                <i class="font-light text-sm pi pi-language mr-2" />
                <span class="font-light text-sm">{{ t("profile-overview.languages.title") }}</span>
            </template>
            <div v-if="profileLanguages.length" class="flex gap-1 mb-2 flex-wrap">
                <span class="flex items-center mr-2">
                    <i class="pi pi-book" v-tooltip="t('profile-overview.languages.profile')" />
                </span>
                <Tag
                    v-for="language in profileLanguages.slice().sort()"
                    :key="language"
                    rounded
                    style="cursor: default"
                    :pt="tagPt()"
                    v-tooltip.bottom="getDisplayName(i18n.global.locale.value, language)"
                >{{ language }}</Tag>
            </div>
            <div v-if="questionnaireLanguages.length" class="flex gap-1 flex-wrap">
                <span class="flex items-center mr-2">
                    <i class="pi pi-pen-to-square" v-tooltip="t('profile-overview.languages.questionnaire')" />
                </span>
                <Tag
                    v-for="language in questionnaireLanguages.slice().sort()"
                    :key="language"
                    rounded
                    style="cursor: default"
                    :pt="tagPt()"
                    v-tooltip.bottom="getDisplayName(i18n.global.locale.value, language)"
                >{{ language }}</Tag>
            </div>
        </Fieldset>
    </template>

    <!-- Normal collapsible mode -->
    <template v-else>
        <div
            class="flex mt-0 mb-1 cursor-pointer text-sm font-light gap-1 underline"
            :id="toggleId"
            @click="toggle"
        >
            {{ t(`profile-overview.product-card.tooltips.languages.${expanded}`) }}
        </div>
        <div v-if="expanded" class="mt-2">
            <Fieldset :id="fieldsetId">
                <template #legend>
                    <i class="font-light text-sm pi pi-language mr-2" />
                    <span class="font-light text-sm">{{ t("profile-overview.languages.title") }}</span>
                </template>
                <div class="flex gap-1 mb-2 flex-wrap">
                    <span class="flex items-center mr-2">
                        <i class="pi pi-book" v-tooltip="t('profile-overview.languages.profile')" />
                    </span>
                    <Tag
                        :data-cy="`language-toggle-profile-${language}`"
                        v-for="language in profileLanguages.slice().sort()"
                        :key="language"
                        rounded
                        style="cursor: default"
                        :pt="tagPt()"
                        v-tooltip.bottom="getDisplayName(i18n.global.locale.value, language)"
                    >{{ language }}</Tag>
                </div>
                <div class="flex gap-1 flex-wrap">
                    <span class="flex items-center mr-2">
                        <i class="pi pi-pen-to-square" v-tooltip="t('profile-overview.languages.questionnaire')" />
                    </span>
                    <Tag
                        :data-cy="`language-toggle-questionnaire-${language}`"
                        v-for="language in questionnaireLanguages.slice().sort()"
                        :key="language"
                        rounded
                        style="cursor: default"
                        :pt="tagPt()"
                        v-tooltip.bottom="getDisplayName(i18n.global.locale.value, language)"
                    >{{ language }}</Tag>
                </div>
            </Fieldset>
        </div>
    </template>
</template>

<style scoped>
.lang-icon {
    font-size: 0.75rem;
    color: var(--p-text-muted-color);
    flex-shrink: 0;
}
</style>
